import { useCallback, useRef, useState } from 'react';
import { Panel, Group as PanelGroup, Separator } from 'react-resizable-panels';
import styled from 'styled-components';

/* ── Root ─────────────────────────────────────────────────────── */

const LayoutRoot = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    height: 100%;
`;

/* Top row (video + subtitles) — grows to fill available space */
const TopRow = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const PanelScrollable = styled.div`
    height: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
`;

/* ── Horizontal handle between Video and Subtitles ────────────── */

const ColHandle = styled(Separator)`
    width: 6px;
    height: 100%;
    cursor: col-resize;
    background-color: #2a2a2a;
    border: none;
    flex-shrink: 0;
    transition: background-color 0.15s;

    &:hover,
    &[data-resize-handle-active] {
        background-color: #4a9eff;
    }
`;

/* ── Row handle bar (between top row and timeline) ────────────── */

const RowHandleBar = styled.div`
    display: flex;
    align-items: center;
    height: 22px;
    background-color: #1e1e1e;
    flex-shrink: 0;
    user-select: none;
    cursor: row-resize;
`;

const DragHint = styled.span`
    flex: 1;
    text-align: center;
    font-size: 10px;
    color: #555;
    letter-spacing: 2px;
    pointer-events: none;
`;

const CollapseBtn = styled.button`
    flex-shrink: 0;
    height: 100%;
    padding: 0 10px;
    background: #2a2a2a;
    border: none;
    border-left: 1px solid #444;
    color: #aaa;
    font-size: 11px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: background-color 0.15s, color 0.15s;
    white-space: nowrap;

    &:first-child {
        border-left: none;
    }

    &:hover {
        background: #4a9eff;
        color: #fff;
    }
`;

/* ── Timeline section ─────────────────────────────────────────── */

const TimelineSection = styled.div`
    flex-shrink: 0;
    overflow: hidden;
    transition: height 0.15s ease;
`;

/* ─────────────────────────────────────────────────────────────────
 * EditorLayout
 *
 * ┌──────────────────┬─────────────────────────┐
 * │   Video (left)   │    Subtitles (right)     │  ← resize by dragging ColHandle
 * ├──────────────────┴─────────────────────────-┤
 * │ ◀ Video  ◀ Subtitles  ⠿⠿⠿⠿  ▼ Timeline    │  ← drag to resize / click to collapse
 * ├─────────────────────────────────────────────┤
 * │       Timeline  (full width, bottom)         │
 * └─────────────────────────────────────────────┘
 * ──────────────────────────────────────────────────────────────── */
export default function EditorLayout({ videoPanel, subtitlePanel, timelinePanel }) {
    /* Refs for the collapsible horizontal panels */
    const videoPanelRef    = useRef(null);
    const subtitlePanelRef = useRef(null);

    const [videoCollapsed,    setVideoCollapsed]    = useState(false);
    const [subtitleCollapsed, setSubtitleCollapsed] = useState(false);

    /* Timeline height — managed by drag + collapse */
    const [timelineHeight, setTimelineHeight] = useState(220);
    const [timelineCollapsed, setTimelineCollapsed] = useState(false);
    const savedTimelineHeight = useRef(220);

    const toggle = (ref) => {
        if (!ref.current) return;
        if (ref.current.isCollapsed()) ref.current.expand();
        else ref.current.collapse();
    };

    const toggleTimeline = () => {
        if (timelineCollapsed) {
            setTimelineHeight(savedTimelineHeight.current || 220);
            setTimelineCollapsed(false);
        } else {
            savedTimelineHeight.current = timelineHeight;
            setTimelineHeight(0);
            setTimelineCollapsed(true);
        }
    };

    /* Drag the row handle bar to resize the timeline height */
    const handleRowDragStart = useCallback((e) => {
        if (e.target.tagName === 'BUTTON') return; // don't drag when clicking a button
        e.preventDefault();
        const startY = e.clientY;
        const startH = timelineCollapsed ? 0 : timelineHeight;

        const onMove = (ev) => {
            const delta = startY - ev.clientY; // drag up → bigger timeline
            const next = Math.max(40, Math.min(window.innerHeight * 0.7, startH + delta));
            setTimelineHeight(next);
            setTimelineCollapsed(false);
            savedTimelineHeight.current = next;
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [timelineCollapsed, timelineHeight]);

    return (
        <LayoutRoot>

            {/* ── Top row: Video (left) + Subtitles (right) ── */}
            <TopRow>
                <PanelGroup direction="horizontal" style={{ height: '100%' }}>

                    <Panel
                        id="v2-video-panel"
                        defaultSize={42}
                        minSize={10}
                        collapsible
                        collapsedSize={0}
                        panelRef={videoPanelRef}
                        onCollapse={() => setVideoCollapsed(true)}
                        onExpand={() => setVideoCollapsed(false)}
                    >
                        <PanelScrollable>{videoPanel}</PanelScrollable>
                    </Panel>

                    <ColHandle id="v2-col-handle" />

                    <Panel
                        id="v2-subtitle-panel"
                        defaultSize={58}
                        minSize={10}
                        collapsible
                        collapsedSize={0}
                        panelRef={subtitlePanelRef}
                        onCollapse={() => setSubtitleCollapsed(true)}
                        onExpand={() => setSubtitleCollapsed(false)}
                    >
                        <PanelScrollable style={{ overflow: 'hidden' }}>{subtitlePanel}</PanelScrollable>
                    </Panel>

                </PanelGroup>
            </TopRow>

            {/* ── Row handle bar — drag to resize, buttons to collapse ── */}
            <RowHandleBar onMouseDown={handleRowDragStart}>
                <CollapseBtn
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggle(videoPanelRef)}
                    title={videoCollapsed ? 'Expand Video' : 'Collapse Video'}
                >
                    {videoCollapsed ? '▶' : '◀'} Video
                </CollapseBtn>

                <CollapseBtn
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => toggle(subtitlePanelRef)}
                    title={subtitleCollapsed ? 'Expand Subtitles' : 'Collapse Subtitles'}
                >
                    {subtitleCollapsed ? '▶' : '◀'} Subtitles
                </CollapseBtn>

                <DragHint>⠿ ⠿ ⠿</DragHint>

                <CollapseBtn
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={toggleTimeline}
                    title={timelineCollapsed ? 'Expand Timeline' : 'Collapse Timeline'}
                >
                    {timelineCollapsed ? '▲ Timeline' : '▼ Timeline'}
                </CollapseBtn>
            </RowHandleBar>

            {/* ── Timeline — full width, bottom ── */}
            <TimelineSection style={{ height: timelineCollapsed ? 0 : timelineHeight }}>
                <div style={{ height: '100%', overflow: 'hidden' }}>
                    {timelinePanel}
                </div>
            </TimelineSection>

        </LayoutRoot>
    );
}
